import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Rocket, RefreshCw, Loader2, CheckCircle2, AlertTriangle, XCircle,
  ChevronDown, ChevronRight, MailCheck, ExternalLink, Save, BellRing, BellOff,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const TONE = {
  pass: { icon: CheckCircle2, color: "text-emerald-400", border: "border-emerald-800/50", label: "Clear" },
  warn: { icon: AlertTriangle, color: "text-amber-400", border: "border-amber-800/50", label: "Attention" },
  fail: { icon: XCircle, color: "text-red-400", border: "border-red-800/60", label: "Blocker" },
};

const euros = (cents) => (cents == null ? "—" : `€${(cents / 100).toLocaleString()}`);

const Detail = ({ label, value, testid }) => (
  <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-slate-800/60 last:border-0" data-testid={testid}>
    <span className="text-slate-500 text-xs">{label}</span>
    <span className="text-slate-300 text-xs text-right">{value}</span>
  </div>
);

const WeekBars = ({ weeks }) => {
  const peak = Math.max(1, ...weeks.map((w) => w.open_slots));
  return (
    <div className="grid grid-cols-4 gap-3 mt-2" data-testid="launch-availability-weeks">
      {weeks.map((week, idx) => (
        <div key={week.from} data-testid={`launch-week-${idx}`}>
          <div className="h-20 flex items-end bg-slate-950/60 border border-slate-800">
            <div
              className={`w-full transition-all ${week.open_slots === 0 ? "bg-red-900/70" : "bg-[#D4AF37]/70"}`}
              style={{ height: `${Math.max(4, (week.open_slots / peak) * 100)}%` }}
            />
          </div>
          <p className="text-white text-sm mt-2">{week.open_slots} open</p>
          <p className="text-slate-600 text-[10px] uppercase tracking-wider">
            {idx === 0 ? "This week" : `Week ${idx + 1}`} · {week.booked} booked
          </p>
        </div>
      ))}
    </div>
  );
};

const LegalEditor = ({ details, onSaved }) => {
  const [form, setForm] = useState({
    address: details.address || "",
    registration: details.registration || "",
    vat_number: details.vat_number || "",
    jurisdiction: details.jurisdiction || "",
    contact_email: details.contact_email || "",
  });
  const [saving, setSaving] = useState(false);

  const save = (extra = {}) => {
    setSaving(true);
    axios
      .put(`${API}/admin/launch/legal`, { ...form, ...extra }, authHeader())
      .then(() => {
        toast.success(extra.reviewed ? "Policies signed off" : "Entity details saved");
        onSaved();
      })
      .catch(() => toast.error("Could not save the entity details"))
      .finally(() => setSaving(false));
  };

  const field = (key, label, placeholder) => (
    <div>
      <label className="text-slate-500 text-[10px] uppercase tracking-[0.2em] block mb-1.5">{label}</label>
      <Input
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        data-testid={`legal-field-${key}`}
        className="bg-slate-950 border-slate-800 text-white rounded-none text-sm"
      />
    </div>
  );

  return (
    <div className="mt-4 space-y-4" data-testid="legal-editor">
      <div className="grid md:grid-cols-2 gap-4">
        {field("address", "Registered address *", "Rua Example 12, 1000-001 Lisbon, Portugal")}
        {field("contact_email", "Contact email *", "bookings@badbossclinic.com")}
        {field("registration", "Company registration", "NIPC 123456789")}
        {field("vat_number", "VAT number", "PT123456789")}
        {field("jurisdiction", "Governing law", "Portugal")}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <Button
          size="sm"
          onClick={() => save()}
          disabled={saving}
          data-testid="legal-save-btn"
          className="bg-[#D4AF37] text-slate-950 hover:bg-[#B5952F] rounded-none text-xs uppercase tracking-widest"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-2" />}
          Save details
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={() => save({ reviewed: true })}
          disabled={saving}
          data-testid="legal-reviewed-btn"
          className="border-slate-700 text-slate-300 hover:text-white rounded-none text-xs"
        >
          I have read and approved the three pages
        </Button>
        {details.reviewed_at && (
          <span className="text-emerald-400 text-xs" data-testid="legal-reviewed-at">
            Signed off {new Date(details.reviewed_at).toLocaleDateString("en-GB")}
          </span>
        )}
      </div>
      <div className="flex flex-wrap gap-4 pt-1">
        {(details.documents || []).map((docItem) => (
          <a
            key={docItem.key}
            href={docItem.path}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-slate-400 hover:text-[#D4AF37] text-xs transition-colors"
            data-testid={`legal-open-${docItem.key}`}
          >
            {docItem.title} <ExternalLink className="w-3 h-3" />
          </a>
        ))}
      </div>
    </div>
  );
};

const CheckDetails = ({ check, onSaved }) => {
  const d = check.details || {};
  if (check.key === "payments") {
    return (
      <div className="grid md:grid-cols-2 gap-x-8">
        <div>
          <Detail label="Mode" value={d.live ? "Live" : `${d.mode} (test cards)`} testid="launch-payments-mode" />
          <Detail label="Charges enabled" value={d.charges_enabled ? "Yes" : "No"} />
          <Detail label="Payouts enabled" value={d.payouts_enabled ? "Yes" : "No"} />
          <Detail label="Webhook secret" value={d.webhook_secret_set ? "Set" : "Missing"} />
        </div>
        <div>
          {(d.prices || []).map((price) => (
            <Detail
              key={price.lookup_key}
              label={price.label}
              testid={`launch-price-${price.lookup_key}`}
              value={
                !price.found ? "Missing" : price.matches_expected ? euros(price.unit_amount) : `${euros(price.unit_amount)} — mismatch`
              }
            />
          ))}
        </div>
      </div>
    );
  }
  if (check.key === "email") {
    return (
      <div className="grid md:grid-cols-2 gap-x-8">
        <div>
          <Detail label="Transport" value={d.transport} testid="launch-email-transport" />
          <Detail label="Sends as" value={d.from_address} />
          <Detail label="Unhandled hard bounces" value={d.unresolved_hard} testid="launch-email-hard" />
          <Detail label="Mailbox scan" value={d.scan_enabled ? "On (every 30 min)" : "Off"} />
        </div>
        <div>
          {(d.probes || []).length === 0 && (
            <p className="text-slate-500 text-xs py-2" data-testid="launch-email-noprobe">
              No delivery check run yet — press “Verify reply addresses”.
            </p>
          )}
          {(d.probes || []).map((probe) => (
            <Detail
              key={probe.address}
              label={`${probe.address} (${probe.role})`}
              testid={`launch-probe-${probe.address}`}
              value={
                probe.deliverable === true ? `Accepted · ${probe.code}`
                  : probe.deliverable === false ? `Refused · ${probe.code}`
                    : "Unknown"
              }
            />
          ))}
        </div>
      </div>
    );
  }
  if (check.key === "calendar") {
    return (
      <div className="md:w-1/2">
        <Detail label="Credentials" value={d.configured ? "In place" : "Not set"} />
        <Detail label="Connected" value={d.connected ? "Yes" : "No"} testid="launch-calendar-connected" />
        <Detail label="Account" value={d.email || "—"} />
        <Detail label="Calendar" value={d.calendar_id || "—"} />
        {d.last_error && <Detail label="Last error" value={d.last_error} />}
      </div>
    );
  }
  if (check.key === "availability") {
    return (
      <div>
        <div className="grid md:grid-cols-2 gap-x-8">
          <div>
            <Detail label="Open slots (4 weeks)" value={d.open_slots} testid="launch-open-slots" />
            <Detail label="Already booked" value={d.booked} />
          </div>
          <div>
            <Detail label="Weekly schedule" value={d.schedule_enabled ? "Running" : "Off"} />
            <Detail label="Horizon" value={d.weeks_ahead ? `${d.weeks_ahead} weeks · ${d.schedule_timezone}` : "—"} />
          </div>
        </div>
        <WeekBars weeks={d.weeks || []} />
      </div>
    );
  }
  return <LegalEditor details={d} onSaved={onSaved} />;
};

const CheckRow = ({ check, onSaved }) => {
  const [open, setOpen] = useState(check.status !== "pass");
  const tone = TONE[check.status] || TONE.warn;
  const Icon = tone.icon;
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className={`border ${tone.border} bg-slate-950/50`} data-testid={`launch-check-${check.key}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start gap-4 p-5 text-left hover:bg-slate-900/40 transition-colors"
        data-testid={`launch-toggle-${check.key}`}
      >
        <Icon className={`w-5 h-5 shrink-0 mt-0.5 ${tone.color}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-white text-sm font-medium">{check.title}</span>
            <span
              className={`text-[10px] uppercase tracking-[0.2em] ${tone.color}`}
              data-testid={`launch-status-${check.key}`}
            >
              {tone.label}
            </span>
          </div>
          <p className="text-slate-500 text-xs mt-1" data-testid={`launch-summary-${check.key}`}>{check.summary}</p>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">{check.action}</p>
        </div>
        <Chevron className="w-4 h-4 text-slate-600 shrink-0 mt-1" />
      </button>
      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-800/60">
          {(check.blockers || []).map((line, i) => (
            <p key={i} className="text-red-300 text-xs leading-relaxed mb-2" data-testid={`launch-blocker-${check.key}-${i}`}>
              ✕ {line}
            </p>
          ))}
          {(check.warnings || []).map((line, i) => (
            <p key={i} className="text-amber-300/90 text-xs leading-relaxed mb-2" data-testid={`launch-warning-${check.key}-${i}`}>
              ! {line}
            </p>
          ))}
          <div className="mt-3">
            <CheckDetails check={check} onSaved={onSaved} />
          </div>
        </div>
      )}
    </div>
  );
};

const WatchdogCard = () => {
  const [state, setState] = useState(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API}/admin/launch/watchdog`, authHeader()).then((res) => setState(res.data)).catch(() => null);
  }, []);

  useEffect(load, [load]);

  const runNow = () => {
    setRunning(true);
    axios
      .post(`${API}/admin/launch/watchdog/run`, {}, authHeader())
      .then((res) => {
        const r = res.data;
        toast.success(
          r.waiting_for_live
            ? "Checked. Still quiet until Stripe goes live."
            : r.emailed
              ? "Checked — an alert email just went out."
              : "Checked. Nothing changed, so nothing was emailed.",
        );
        load();
      })
      .catch(() => toast.error("The watchdog sweep failed"))
      .finally(() => setRunning(false));
  };

  if (!state) return null;
  const armed = state.armed;

  return (
    <div className="border border-slate-800 bg-slate-950/60 p-5 mb-6" data-testid="watchdog-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          {armed ? <BellRing className="w-4 h-4 text-emerald-400 mt-0.5" /> : <BellOff className="w-4 h-4 text-slate-500 mt-0.5" />}
          <div>
            <p className="text-white text-sm" data-testid="watchdog-state">
              {armed ? "Watchdog armed" : "Watchdog waiting for Stripe to go live"}
            </p>
            <p className="text-slate-500 text-xs mt-1 max-w-xl leading-relaxed">
              {armed
                ? `Checked every ${state.interval_minutes} minutes. ${state.recipient} gets an email the moment a check turns red, and a short note when it clears.`
                : `Deliberately quiet while payments are still in test mode. The first sweep that sees a live Stripe account takes a baseline and starts alerting ${state.recipient}.`}
            </p>
            {state.last_run_at && (
              <p className="text-slate-600 text-[11px] mt-2" data-testid="watchdog-last-run">
                Last checked {new Date(state.last_run_at).toLocaleString("en-GB")}
              </p>
            )}
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={runNow}
          disabled={running}
          data-testid="watchdog-run-btn"
          className="border-slate-700 text-slate-300 hover:text-white rounded-none text-xs"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : null}
          Check now
        </Button>
      </div>
      {(state.alerts || []).length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-800/60 space-y-2" data-testid="watchdog-alerts">
          {state.alerts.slice(0, 5).map((alert, idx) => (
            <p key={idx} className="text-xs leading-relaxed" data-testid={`watchdog-alert-${idx}`}>
              <span className={alert.kind === "red" ? "text-red-400" : alert.kind === "recovered" ? "text-emerald-400" : "text-slate-400"}>
                {alert.kind === "red" ? "Red" : alert.kind === "recovered" ? "Cleared" : "Armed"}
              </span>
              <span className="text-slate-600"> · {new Date(alert.at).toLocaleString("en-GB")} · </span>
              <span className="text-slate-400">{alert.note}</span>
            </p>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminLaunchTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [probing, setProbing] = useState(false);

  const load = useCallback((probe = false) => {
    if (probe) setProbing(true);
    else setLoading(true);
    axios
      .get(`${API}/admin/launch/readiness${probe ? "?probe=true" : ""}`, authHeader())
      .then((res) => setData(res.data))
      .catch(() => toast.error("Could not run the launch checks"))
      .finally(() => {
        setLoading(false);
        setProbing(false);
      });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return (
      <div className="bg-slate-900/50 border border-slate-800 p-6 flex items-center gap-3 text-slate-400 text-sm">
        <Loader2 className="w-4 h-4 animate-spin" /> Running the launch checks…
      </div>
    );
  }
  if (!data) return null;

  const { counts, ready, all_clear: allClear } = data;
  const verdict = allClear
    ? { text: "Ready to advertise", cls: "border-emerald-700/60 text-emerald-300 bg-emerald-950/30" }
    : ready
      ? { text: `${counts.warn} thing${counts.warn === 1 ? "" : "s"} to tidy`, cls: "border-amber-700/60 text-amber-300 bg-amber-950/30" }
      : { text: `${counts.fail} blocker${counts.fail === 1 ? "" : "s"}`, cls: "border-red-700/60 text-red-300 bg-red-950/30" };

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="launch-readiness-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-2">
        <h2 className="font-serif text-xl text-white flex items-center gap-2">
          <Rocket className="w-5 h-5 text-[#D4AF37]" />
          Launch readiness
        </h2>
        <div className="flex items-center gap-3">
          <span className={`text-[10px] uppercase tracking-[0.2em] border px-3 py-1 ${verdict.cls}`} data-testid="launch-verdict">
            {verdict.text}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => load(true)}
            disabled={probing}
            data-testid="launch-probe-btn"
            className="border-slate-700 text-slate-300 hover:text-white rounded-none text-xs"
          >
            {probing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-2" /> : <MailCheck className="w-3.5 h-3.5 mr-2" />}
            Verify reply addresses
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => load()}
            data-testid="launch-refresh"
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </Button>
        </div>
      </div>

      <p className="text-slate-400 text-sm mb-6 max-w-2xl">
        Five things have to be true before paid traffic is worth spending on. A blocker means a visitor's money,
        email or booking would fail today.
      </p>

      <div className="flex items-center gap-4 mb-6" data-testid="launch-score">
        <div className="flex-1 h-1.5 bg-slate-800 overflow-hidden">
          <div
            className="h-full bg-[#D4AF37] transition-all"
            style={{ width: `${(counts.pass / counts.total) * 100}%` }}
          />
        </div>
        <span className="text-slate-400 text-xs whitespace-nowrap">
          {counts.pass} of {counts.total} clear
        </span>
      </div>

      <WatchdogCard />

      <div className="space-y-4">
        {data.checks.map((check) => (
          <CheckRow key={check.key} check={check} onSaved={() => load()} />
        ))}
      </div>
    </div>
  );
};
